import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend,
  PieChart, Pie, Cell
} from 'recharts'
import LoadingProgress from '../components/LoadingProgress'
import ErrorMessage from '../components/ErrorMessage'
import { fetchArqueos } from '../actions/arqueos'
import '../styles/DashboardView.css'

const DONUT_COLORS = ['#0b97ff', '#06b6d4', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#f97316', '#ef4444']
const ITEM_NAMES = ['VWPACK', 'VWLAY', 'VWGO', 'VWCHARGER']

function fmtCurrency(v) {
  if (v === null || v === undefined) return '—'
  return Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
}

function localIso(d) {
  if (!(d instanceof Date) || isNaN(d)) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

function shiftYear(iso, delta) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d)) return ''
  d.setFullYear(d.getFullYear() + delta)
  return localIso(d)
}

class ArqueosDashboardView extends React.Component {
  constructor(props) {
    super(props)
    const today = new Date()
    const monthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
    const monthEnd = new Date(today.getFullYear(), today.getMonth(), 0)
    const s = localIso(monthStart)
    const e = localIso(monthEnd)
    this.state = {
      startDate: s,
      endDate: e,
      compareStartDate: shiftYear(s, -1),
      compareEndDate: shiftYear(e, -1),
      tempStartDate: s,
      tempEndDate: e,
      tempCompareStartDate: shiftYear(s, -1),
      tempCompareEndDate: shiftYear(e, -1),
      siteFilter: '',
      showAdvancedFilters: false,
      lyArqueos: null,
      hiddenLines: {},
    }
  }

  componentDidMount() {
    this.load()
  }

  fillLastYear = () => {
    const { tempStartDate, tempEndDate } = this.state
    this.setState({
      tempCompareStartDate: shiftYear(tempStartDate, -1),
      tempCompareEndDate: shiftYear(tempEndDate, -1),
    })
  }

  load = async () => {
    const { startDate, endDate, compareStartDate, compareEndDate } = this.state
    let lyRows = null
    if (compareStartDate && compareEndDate) {
      try {
        const lyAction = await this.props.fetchArqueos(undefined, undefined, compareStartDate, compareEndDate)
        lyRows = (lyAction && lyAction.response) || null
      } catch (e) { lyRows = null }
    }
    this.setState({ lyArqueos: lyRows })
    this.props.fetchArqueos(undefined, undefined, startDate, endDate)
  }

  handleLegendClick = (data) => {
    const key = data.dataKey
    this.setState(prev => ({
      hiddenLines: { ...prev.hiddenLines, [key]: !prev.hiddenLines[key] },
    }))
  }

  handleRefresh = () => {
    this.setState(
      {
        startDate: this.state.tempStartDate,
        endDate: this.state.tempEndDate,
        compareStartDate: this.state.tempCompareStartDate,
        compareEndDate: this.state.tempCompareEndDate,
      },
      this.load
    )
  }

  // ─── Aggregations ─────────────────────────────────────────────────────────

  filterRows(rows) {
    const { siteFilter } = this.state
    if (!siteFilter.trim()) return rows
    const q = siteFilter.trim().toLowerCase()
    return rows.filter(r =>
      ((r.Customer && r.Customer.Fullname) || '').toLowerCase().includes(q) ||
      (r.Serial || '').toLowerCase().includes(q)
    )
  }

  getRows() { return this.filterRows(Array.isArray(this.props.arqueos) ? this.props.arqueos : []) }
  getLYRows() { return this.filterRows(Array.isArray(this.state.lyArqueos) ? this.state.lyArqueos : []) }

  aggregateKPIs(rows) {
    const gmv = rows.reduce((s, r) => s + (Number(r.SalesTotal || 0) || 0), 0)
    const commission = rows.reduce((s, r) => {
      const ic = r.ItemsComissions || {}
      return s + Object.values(ic).reduce((a, v) => a + (Number(v) || 0), 0)
    }, 0)
    const transactions = rows.reduce((s, r) => s + (Number(r.TotalTickets || 0) || 0), 0)
    const ticketAvg = transactions > 0 ? gmv / transactions : null
    const machines = new Set(rows.map(r => r.Serial).filter(Boolean)).size
    return { gmv, commission, transactions, ticketAvg, machines }
  }

  buildDailyTrend(rows, lyRows) {
    const byDay = {}
    rows.forEach(r => {
      const day = r.TimeStamp ? String(r.TimeStamp).slice(0, 10) : 'unknown'
      if (!byDay[day]) byDay[day] = { day, gmv: 0, transactions: 0, lyGmv: null, lyTransactions: null }
      byDay[day].gmv += (Number(r.SalesTotal || 0) || 0)
      byDay[day].transactions += (Number(r.TotalTickets || 0) || 0)
    })
    if (lyRows && lyRows.length > 0) {
      lyRows.forEach(r => {
        const rawDay = r.TimeStamp ? String(r.TimeStamp).slice(0, 10) : null
        if (!rawDay) return
        const shiftedDay = shiftYear(rawDay, 1)
        if (!byDay[shiftedDay]) byDay[shiftedDay] = { day: shiftedDay, gmv: 0, transactions: 0, lyGmv: 0, lyTransactions: 0 }
        if (byDay[shiftedDay].lyGmv === null) byDay[shiftedDay].lyGmv = 0
        if (byDay[shiftedDay].lyTransactions === null) byDay[shiftedDay].lyTransactions = 0
        byDay[shiftedDay].lyGmv += (Number(r.SalesTotal || 0) || 0)
        byDay[shiftedDay].lyTransactions += (Number(r.TotalTickets || 0) || 0)
      })
    }
    return Object.values(byDay).sort((a, b) => a.day.localeCompare(b.day))
  }

  buildMachineRanking(rows) {
    const byMachine = {}
    rows.forEach(r => {
      const key = r.Serial || '—'
      const customerName = (r.Customer && r.Customer.Fullname) || null
      const label = customerName ? `${customerName} · ${key}` : key
      if (!byMachine[key]) byMachine[key] = { machine: label, gmv: 0, transactions: 0 }
      byMachine[key].gmv += (Number(r.SalesTotal || 0) || 0)
      byMachine[key].transactions += (Number(r.TotalTickets || 0) || 0)
    })
    return Object.values(byMachine).sort((a, b) => b.gmv - a.gmv).slice(0, 15)
  }

  buildItemBreakdown(rows) {
    const counts = {}
    ITEM_NAMES.forEach(k => { counts[k] = 0 })
    rows.forEach(r => {
      const items = r.Items || {}
      ITEM_NAMES.forEach(k => { counts[k] += (Number(items[k] || 0) || 0) })
    })
    return ITEM_NAMES.map(k => ({ name: k, value: counts[k] })).filter(d => d.value > 0)
  }

  buildPaymentBreakdown(rows) {
    const totals = { TPV: 0, CASH: 0, QR: 0 }
    rows.forEach(r => {
      totals.TPV  += (Number(r.SalesTPV  || 0) || 0)
      totals.CASH += (Number(r.SalesCash || 0) || 0)
      totals.QR   += (Number(r.SalesQR   || 0) || 0)
    })
    return Object.entries(totals).map(([name, value]) => ({ name, value })).filter(d => d.value > 0)
  }

  // ─── Dual-ring donut card ─────────────────────────────────────────────────
  renderDonutCard(title, icon, currentData, lyData) {
    const total = currentData.reduce((s, d) => s + d.value, 0)
    const lyTotal = lyData.reduce((s, d) => s + d.value, 0)
    const hasLY = lyTotal > 0

    return (
      <div className="dashboard-card-section">
        <div className="card-header-custom">
          <i className={`fas fa-${icon} mr-2`}></i>
          {title}
          {hasLY && <span style={{ marginLeft: 8, fontSize: 11, color: '#6b7280', fontWeight: 400 }}>— año anterior (interior)</span>}
        </div>
        <div className="card-body-custom">
          {currentData.length === 0 ? (
            <p className="text-muted text-center py-4">Sin datos</p>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              {/* Donut */}
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 220, height: 220 }}>
                <PieChart width={220} height={220}>
                  {/* outer ring: current */}
                  <Pie data={currentData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    innerRadius={65} outerRadius={90} paddingAngle={2}
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: '#6b7280', strokeDasharray: '3 4', strokeWidth: 1 }}
                  >
                    {currentData.map((entry, i) => <Cell key={entry.name} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                  </Pie>
                  {/* inner ring: LY */}
                  {hasLY && (
                    <Pie data={lyData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                      innerRadius={40} outerRadius={55} paddingAngle={1}
                      label={false} labelLine={false}
                    >
                      {lyData.map((entry, i) => <Cell key={entry.name} fill={DONUT_COLORS[i % DONUT_COLORS.length]} fillOpacity={0.45} />)}
                    </Pie>
                  )}
                  <Tooltip formatter={(v, name) => [`${v} (${total > 0 ? ((v / total) * 100).toFixed(1) : 0}%)`, name]} />
                </PieChart>
                {/* central overlay */}
                <div style={{ position: 'absolute', textAlign: 'center', pointerEvents: 'none' }}>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{total.toLocaleString('es-ES')}</div>
                  {hasLY && <div style={{ fontSize: 11, color: '#6b7280' }}>vs {lyTotal.toLocaleString('es-ES')}</div>}
                  <div style={{ fontSize: 11, color: '#6b7280' }}>Total</div>
                </div>
              </div>
              {/* Legend with LY values */}
              <div style={{ flex: 1, minWidth: 120 }}>
                {currentData.map((d, i) => {
                  const lyEntry = lyData.find(l => l.name === d.name)
                  return (
                    <div key={d.name} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 12, height: 12, background: DONUT_COLORS[i % DONUT_COLORS.length], borderRadius: 3, marginTop: 3, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{d.name}</div>
                        <div className="text-muted small">
                          {d.value.toLocaleString('es-ES')} ({total > 0 ? ((d.value / total) * 100).toFixed(1) : 0}%)
                          {lyEntry && lyEntry.value > 0 && (
                            <> vs {lyEntry.value.toLocaleString('es-ES')} ({lyTotal > 0 ? ((lyEntry.value / lyTotal) * 100).toFixed(1) : 0}%)</>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  render() {
    const { role, loading, error } = this.props
    const {
      startDate, endDate, compareStartDate, compareEndDate,
      tempStartDate, tempEndDate, tempCompareStartDate, tempCompareEndDate,
      siteFilter, showAdvancedFilters,
      hiddenLines,
    } = this.state

    if (role !== 'ADMIN') return <ErrorMessage message="Acceso restringido a administradores" />
    if (error) return <ErrorMessage message={error.message || 'Error al cargar datos'} />

    const rows = this.getRows()
    const lyRows = this.getLYRows()
    const kpis = this.aggregateKPIs(rows)
    const lyKpis = this.aggregateKPIs(lyRows)
    const hasLY = lyRows.length > 0
    const hasCompareRange = !!(compareStartDate && compareEndDate)
    const dailyTrend = this.buildDailyTrend(rows, lyRows)
    const hasLyTrend = dailyTrend.some(d => d.lyGmv !== null)
    const hasLyTransTrend = dailyTrend.some(d => d.lyTransactions !== null)
    const machineRanking = this.buildMachineRanking(rows)
    const itemBreakdown = this.buildItemBreakdown(rows)
    const itemBreakdownLY = this.buildItemBreakdown(lyRows)
    const paymentBreakdown = this.buildPaymentBreakdown(rows)
    const paymentBreakdownLY = this.buildPaymentBreakdown(lyRows)

    const customerOptions = (() => {
      const all = Array.isArray(this.props.arqueos) ? this.props.arqueos : []
      const names = all.map(r => (r.Customer && r.Customer.Fullname) || '').filter(v => v.trim())
      return [...new Set(names)].sort()
    })()

    const periodTitle = startDate ? `${startDate} → ${endDate}` : '(sin período)'
    const compareTitle = compareStartDate
      ? hasLY ? `${compareStartDate} → ${compareEndDate}` : `${compareStartDate} → ${compareEndDate} (sin datos)`
      : '(sin comparación)'

    // KPI delta badge
    const renderDelta = (cur, ly, isCurrency = true) => {
      if (!hasCompareRange || !hasLY || ly == null || ly === 0) return <div className="small text-muted">vs LY: n/a</div>
      const delta = cur - ly
      const pctVal = ly !== 0 ? (delta / ly) * 100 : null
      const colorClass = delta > 0 ? 'text-success' : delta < 0 ? 'text-danger' : 'text-muted'
      const arrow = delta > 0 ? '▲' : delta < 0 ? '▼' : ''
      return (
        <div className="small" style={{ marginTop: 2 }}>
          <span className="text-muted">vs LY:&nbsp;</span>
          <span className={colorClass}>
            {arrow} {isCurrency
              ? `${delta >= 0 ? '+' : '-'}${fmtCurrency(Math.abs(delta))}`
              : `${delta >= 0 ? '+' : ''}${Math.round(delta).toLocaleString('es-ES')}`}
            {pctVal !== null ? ` (${pctVal >= 0 ? '+' : ''}${pctVal.toFixed(1)}%)` : ''}
          </span>
        </div>
      )
    }

    // KPI card — lockers style (flex-column: icon+label / value / delta)
    const makeCard = (cls, icon, label, valueFormatted, curRaw, lyRaw, isCurrency = true) => (
      <div
        className={`dashboard-kpi-card ${cls}`}
        key={label}
        style={{ minHeight: 96, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '10px 12px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className={`fas fa-${icon}`} style={{ fontSize: 12, color: '#6b7280' }} />
          <div className="kpi-label" style={{ margin: 0, fontSize: 12 }}>{label}</div>
        </div>
        <div style={{ marginTop: 8 }}>
          <div className="kpi-value" style={{ fontSize: 20, fontWeight: 700 }}>{valueFormatted}</div>
        </div>
        <div style={{ marginTop: 8 }}>
          {renderDelta(curRaw, lyRaw, isCurrency)}
        </div>
      </div>
    )

    return (
      <div className="dashboard-view">

        {/* ── Header ── */}
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-title">
              <i className="fas fa-chart-line"></i> Dashboard Ventas
            </h1>
            <div className="small text-muted mt-1">
              Período: {periodTitle}&nbsp;(vs {compareTitle})
            </div>
          </div>

          <div className="dashboard-actions">
            <input
              type="text"
              list="arqueos-customer-options"
              className="dashboard-filter-input"
              placeholder="Filtrar máquina / cliente..."
              value={siteFilter}
              onChange={e => this.setState({ siteFilter: e.target.value })}
              style={{ width: 220 }}
            />
            <datalist id="arqueos-customer-options">
              {customerOptions.map(c => <option key={c} value={c} />)}
            </datalist>
            <button
              type="button"
              className="dashboard-filter-input toggle-filters"
              onClick={() => this.setState({ showAdvancedFilters: !showAdvancedFilters })}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
            >
              {showAdvancedFilters
                ? <><i className="fas fa-chevron-up" style={{ marginRight: 6 }} />Ocultar filtros</>
                : <><i className="fas fa-chevron-down" style={{ marginRight: 6 }} />Mostrar filtros</>
              }
            </button>
            <button className="btn btn-primary btn-sm" onClick={this.handleRefresh} disabled={loading}>
              {loading ? <i className="fas fa-spinner fa-spin mr-1" /> : <i className="fas fa-sync mr-1" />}
              Actualizar
            </button>
          </div>
        </div>

        {/* ── Advanced filters panel ── */}
        {showAdvancedFilters && (
          <div className="dashboard-header mb-3" style={{ padding: '12px 20px', justifyContent: 'center' }}>
            <div className="d-flex align-items-center flex-wrap justify-content-center" style={{ gap: 8 }}>
              <span className="small text-muted" style={{ whiteSpace: 'nowrap' }}>Período:</span>
              <input type="date" className="dashboard-filter-input" value={tempStartDate} onChange={e => this.setState({ tempStartDate: e.target.value })} style={{ width: 140 }} />
              <span className="small text-muted">→</span>
              <input type="date" className="dashboard-filter-input" value={tempEndDate} onChange={e => this.setState({ tempEndDate: e.target.value })} style={{ width: 140 }} />
              <span className="small text-muted" style={{ whiteSpace: 'nowrap', marginLeft: 12 }}>
                comp. <em style={{ fontSize: 11 }}>(dejar vacío para desactivar)</em>
              </span>
              <button
                className="btn btn-sm btn-secondary"
                style={{ padding: '0 8px', fontSize: '0.8rem', height: 36 }}
                title="Autofill mismo período año anterior"
                onClick={this.fillLastYear}
                disabled={!tempStartDate}
              >
                Last Year
              </button>
              <input type="date" className="dashboard-filter-input" value={tempCompareStartDate} onChange={e => this.setState({ tempCompareStartDate: e.target.value })} style={{ width: 140 }} />
              <span className="small text-muted">→</span>
              <input type="date" className="dashboard-filter-input" value={tempCompareEndDate} onChange={e => this.setState({ tempCompareEndDate: e.target.value })} style={{ width: 140 }} />
            </div>
          </div>
        )}

        {loading && <LoadingProgress />}

        {!loading && (
          <>
            {/* ── KPI Cards ── */}
            <div className="dashboard-kpi-row">
              {makeCard('total',      'coins',            'GMV Neto',        fmtCurrency(kpis.gmv),                                  kpis.gmv,          lyKpis.gmv,          true)}
              {makeCard('green-block','hand-holding-usd', 'Comisiones',      fmtCurrency(kpis.commission),                           kpis.commission,   lyKpis.commission,   true)}
              {makeCard('pinpad-active','receipt',        'Ticket Medio',    kpis.ticketAvg !== null ? fmtCurrency(kpis.ticketAvg) : '—', kpis.ticketAvg ?? 0, lyKpis.ticketAvg ?? 0, true)}
              {makeCard('pinpad-block','ticket-alt',      'Transacciones',   kpis.transactions.toLocaleString('es-ES'),               kpis.transactions, lyKpis.transactions, false)}
              {makeCard('door-block', 'store',            'Máquinas activas',kpis.machines.toLocaleString('es-ES'),                  kpis.machines,     lyKpis.machines,     false)}
            </div>

            {/* ── Daily Trend ── */}
            <div className="dashboard-card-section">
              <div className="card-header-custom">
                <i className="fas fa-chart-line mr-2"></i>
                Evolución diaria
                {hasLY && <span style={{ marginLeft: 8, fontSize: 11, color: '#6b7280', fontWeight: 400 }}>— año anterior (punteado)</span>}
              </div>
              <div className="card-body-custom">
                {dailyTrend.length === 0 ? (
                  <p className="text-muted text-center py-4">Sin datos para el período seleccionado</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={dailyTrend} margin={{ top: 4, right: 20, bottom: 4, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                      <Tooltip
                        formatter={(v, name) => (name === 'GMV Neto (€)' || name === 'GMV LY (€)') ? fmtCurrency(v) : v != null ? v.toLocaleString('es-ES') : v}
                        labelFormatter={l => `Día: ${l}`}
                      />
                      <Legend onClick={this.handleLegendClick} wrapperStyle={{ cursor: 'pointer' }} formatter={(value, entry) => (
                        <span style={{ color: hiddenLines[entry.dataKey] ? '#aaa' : entry.color, textDecoration: hiddenLines[entry.dataKey] ? 'line-through' : 'none' }}>{value}</span>
                      )} />
                      <Line yAxisId="left" type="monotone" dataKey="gmv" stroke="#0b97ff" strokeWidth={2} dot={false} name="GMV Neto (€)" hide={!!hiddenLines['gmv']} />
                      <Line yAxisId="right" type="monotone" dataKey="transactions" stroke="#7c3aed" strokeWidth={2} dot={false} name="Transacciones" hide={!!hiddenLines['transactions']} />
                      {hasLyTrend && (
                        <Line yAxisId="left" type="monotone" dataKey="lyGmv" stroke="#0b97ff" strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="GMV LY (€)" connectNulls hide={!!hiddenLines['lyGmv']} />
                      )}
                      {hasLyTransTrend && (
                        <Line yAxisId="right" type="monotone" dataKey="lyTransactions" stroke="#7c3aed" strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="Transacciones LY" connectNulls hide={!!hiddenLines['lyTransactions']} />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* ── Donut Charts ── */}
            <div className="dashboard-graphs-grid">
              {this.renderDonutCard('Unidades por producto', 'box', itemBreakdown, itemBreakdownLY)}
              {this.renderDonutCard('Desglose por método de pago', 'credit-card', paymentBreakdown, paymentBreakdownLY)}
            </div>

            {/* ── Top Machines by GMV ── */}
            <div className="dashboard-card-section">
              <div className="card-header-custom">
                <i className="fas fa-trophy mr-2"></i>
                Ranking de máquinas por GMV
              </div>
              <div className="card-body-custom" style={{ padding: 0 }}>
                {machineRanking.length === 0 ? (
                  <p className="text-muted text-center py-4">Sin datos</p>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={Math.min(40 + machineRanking.length * 32, 400)}>
                      <BarChart data={machineRanking} layout="vertical" margin={{ top: 4, right: 30, bottom: 4, left: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="machine" width={200} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={v => fmtCurrency(v)} />
                        <Bar dataKey="gmv" fill="#0b97ff" name="GMV Neto (€)" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                    <div style={{ padding: 16 }}>
                      <table className="dashboard-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Máquina</th>
                            <th style={{ textAlign: 'right' }}>GMV Neto</th>
                            <th style={{ textAlign: 'right' }}>Transacciones</th>
                            <th style={{ textAlign: 'right' }}>Ticket Medio</th>
                          </tr>
                        </thead>
                        <tbody>
                          {machineRanking.map((row, i) => (
                            <tr key={row.machine}>
                              <td style={{ color: '#6b7280' }}>{i + 1}</td>
                              <td style={{ fontWeight: 500 }}>{row.machine}</td>
                              <td style={{ textAlign: 'right' }}>{fmtCurrency(row.gmv)}</td>
                              <td style={{ textAlign: 'right' }}>{row.transactions.toLocaleString('es-ES')}</td>
                              <td style={{ textAlign: 'right' }}>{row.transactions > 0 ? fmtCurrency(row.gmv / row.transactions) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>

          </>
        )}
      </div>
    )
  }
}

const mapStateToProps = state => ({
  role: state.auth.role,
  arqueos: state.arqueos.arqueos,
  loading: state.arqueos.loading,
  error: state.arqueos.error,
})

const mapDispatchToProps = dispatch => ({
  fetchArqueos: bindActionCreators(fetchArqueos, dispatch),
})

export default connect(mapStateToProps, mapDispatchToProps)(ArqueosDashboardView)
